import { AuthContext } from '@urbanx/agx.auth.client';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxModal,
  AgxNotificationBox,
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  type RadioGroupOptions,
} from '@urbanx/agx.ui-components2';
import {
  DocumentTypes,
  StaffCreateContractDepositReleaseRequestDestinationOfTransfer,
  type StaffCreateContractDepositReleaseRequest,
  type StaffGetContractDepositsResponse,
  type UploadedDocument,
  useStaffCreateContractDepositRelease,
} from '@urbanx/agx.campaigns.client';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { deleteFile, getFileLink, uploadFile } from 'services';
import { getEarlyReleaseAmountError } from '../depositCalculations';

const destinationOptions: RadioGroupOptions[] = [
  {
    label: 'Vendor',
    value: StaffCreateContractDepositReleaseRequestDestinationOfTransfer.Vendor,
  },
  {
    label: "Vendor's Solicitor",
    value: StaffCreateContractDepositReleaseRequestDestinationOfTransfer.VendorSolicitor,
  },
  {
    label: 'Other',
    value: StaffCreateContractDepositReleaseRequestDestinationOfTransfer.Other,
  },
];

interface EarlyReleaseModalProps {
  isOpen: boolean;
  availableAmount: number;
  onClose: () => void;
}

const buildRenamedFormData = (contractId: string, file: File) => {
  const renamedFile = new File([file], `${contractId}/${crypto.randomUUID()}/${file.name}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
  const renamedFormData = new FormData();
  renamedFormData.append('File', renamedFile);
  return renamedFormData;
};

const EarlyReleaseModal = ({ isOpen, availableAmount, onClose }: EarlyReleaseModalProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const { campaign } = useSelectedCampaign();
  const { contract } = useSelectedContract();
  const queryClient = useQueryClient();

  const [buyerSolicitorConfirmationFiles, setBuyerSolicitorConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [vendorSolicitorConfirmationFiles, setVendorSolicitorConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [amountInput, setAmountInput] = useState('');
  const [destinationOfTransfer, setDestinationOfTransfer] = useState<
    StaffCreateContractDepositReleaseRequestDestinationOfTransfer | undefined
  >();
  const [otherRecipient, setOtherRecipient] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [cleanupError, setCleanupError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [releaseSubmitted, setReleaseSubmitted] = useState(false);
  const submissionId = useRef<string | undefined>(undefined);
  const wasOpen = useRef(false);
  const processing = useRef(false);
  const uploads = useRef(new Map<string, UploadedDocument>());
  const submittedUploadPaths = useRef(new Set<string>());
  const uploadAuthToken = useRef<string | undefined>(undefined);

  const { staffCreateContractDepositRelease, staffCreateContractDepositReleasePending } =
    useStaffCreateContractDepositRelease({
      onSuccess: () => {},
      onError: () => {},
    });

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      submissionId.current = crypto.randomUUID();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  const amountError = getEarlyReleaseAmountError(amountInput, availableAmount);
  const trimmedOtherRecipient = otherRecipient.trim();
  const otherRecipientError = (() => {
    if (
      destinationOfTransfer !== StaffCreateContractDepositReleaseRequestDestinationOfTransfer.Other
    ) {
      return undefined;
    }
    if (!trimmedOtherRecipient) return 'Enter the recipient';
    if (trimmedOtherRecipient.length > 200) return 'Recipient must be 200 characters or less';
    return undefined;
  })();
  const canSubmit =
    buyerSolicitorConfirmationFiles.length > 0 &&
    vendorSolicitorConfirmationFiles.length > 0 &&
    !amountError &&
    destinationOfTransfer != null &&
    !otherRecipientError &&
    !releaseSubmitted &&
    !isProcessing &&
    !staffCreateContractDepositReleasePending;

  const rememberUpload = (
    document: UploadedDocument,
    setFiles: (files: (UploadedDocument | File)[]) => void
  ) => {
    if (!document.containerFilePath) {
      throw new Error('An uploaded early release document requires a container file path');
    }

    uploads.current.set(document.containerFilePath, document);
    setFiles([document]);
    return document;
  };

  const uploadSelectedFile = async (
    authToken: string,
    documentType: DocumentTypes,
    selectedFile: UploadedDocument | File | undefined,
    setFiles: (files: (UploadedDocument | File)[]) => void
  ) => {
    if (!campaign?.campaignId || !contract?.contractId || !selectedFile) return undefined;
    if (!(selectedFile instanceof File)) return selectedFile;

    const uploadedDocument = await uploadFile(
      authToken,
      buildRenamedFormData(contract.contractId, selectedFile),
      campaign.campaignId,
      documentType,
      null,
      null,
      undefined,
      contract.contractId
    );

    return uploadedDocument ? rememberUpload(uploadedDocument, setFiles) : undefined;
  };

  const cleanUpUploads = async () => {
    const containerFilePaths = [...uploads.current.keys()].filter(
      (path) => !submittedUploadPaths.current.has(path)
    );
    if (containerFilePaths.length > 0) {
      const authToken = uploadAuthToken.current ?? (await getAuthToken());
      if (!authToken) throw new Error('Authentication is required to remove confirmation PDFs.');

      for (const containerFilePath of containerFilePaths) {
        const status = await deleteFile(authToken, containerFilePath);
        if (status !== 200) throw new Error('Confirmation PDF removal failed.');

        uploads.current.delete(containerFilePath);
        setBuyerSolicitorConfirmationFiles((files) =>
          files.filter(
            (file) => file instanceof File || file.containerFilePath !== containerFilePath
          )
        );
        setVendorSolicitorConfirmationFiles((files) =>
          files.filter(
            (file) => file instanceof File || file.containerFilePath !== containerFilePath
          )
        );
      }
    }
  };

  const closeAndCleanUp = async () => {
    if (processing.current) return;
    processing.current = true;
    setIsProcessing(true);
    setSubmitError(undefined);
    setCleanupError(undefined);

    try {
      await cleanUpUploads();
      onClose();
    } catch {
      setCleanupError(
        releaseSubmitted
          ? 'The release was submitted, but unused confirmation PDFs could not be removed. Select Close to retry.'
          : 'Confirmation PDFs could not be removed. Select Cancel to retry.'
      );
    } finally {
      processing.current = false;
      setIsProcessing(false);
    }
  };

  const handleConfirm = async () => {
    setShowValidation(true);
    setSubmitError(undefined);
    setCleanupError(undefined);

    const campaignId = campaign?.campaignId;
    const contractId = contract?.contractId;
    if (!canSubmit || processing.current || !campaignId || !contractId || !submissionId.current) {
      return;
    }

    processing.current = true;
    setIsProcessing(true);

    try {
      const authToken = await getAuthToken();
      if (!authToken) {
        setSubmitError('Authentication is required to upload the confirmation PDFs.');
        return;
      }
      uploadAuthToken.current = authToken;

      const [buyerSolicitorConfirmation, vendorSolicitorConfirmation] = await Promise.all([
        uploadSelectedFile(
          authToken,
          DocumentTypes.BuyerSolicitorEarlyDeposit,
          buyerSolicitorConfirmationFiles[0],
          setBuyerSolicitorConfirmationFiles
        ),
        uploadSelectedFile(
          authToken,
          DocumentTypes.VendorSolicitorEarlyDeposit,
          vendorSolicitorConfirmationFiles[0],
          setVendorSolicitorConfirmationFiles
        ),
      ]);

      if (!buyerSolicitorConfirmation || !vendorSolicitorConfirmation) {
        setSubmitError('Both solicitor confirmation PDFs must upload successfully.');
        return;
      }

      const request: StaffCreateContractDepositReleaseRequest = {
        submissionId: submissionId.current,
        amount: Number(amountInput.replaceAll(',', '')),
        destinationOfTransfer,
        otherRecipient:
          destinationOfTransfer ===
          StaffCreateContractDepositReleaseRequestDestinationOfTransfer.Other
            ? trimmedOtherRecipient
            : undefined,
        buyerSolicitorConfirmation,
        vendorSolicitorConfirmation,
      };

      // A failed response can follow a committed release, so submitted evidence is never cleaned up here.
      for (const { containerFilePath } of [
        buyerSolicitorConfirmation,
        vendorSolicitorConfirmation,
      ]) {
        if (containerFilePath) submittedUploadPaths.current.add(containerFilePath);
      }

      const deposits = await staffCreateContractDepositRelease({
        campaignId,
        contractId,
        body: request,
      });

      setReleaseSubmitted(true);
      for (const { containerFilePath } of [
        buyerSolicitorConfirmation,
        vendorSolicitorConfirmation,
      ]) {
        if (containerFilePath) uploads.current.delete(containerFilePath);
      }
      // Success with this submission ID proves earlier, different requests did not commit.
      submittedUploadPaths.current.clear();
      queryClient.setQueryData<StaffGetContractDepositsResponse>(
        ['staffGetContractDeposits', campaignId, contractId],
        deposits
      );
      try {
        await cleanUpUploads();
      } catch {
        setCleanupError(
          'The release was submitted, but unused confirmation PDFs could not be removed. Select Close to retry.'
        );
        return;
      }
      onClose();
    } catch {
      setSubmitError(
        'Early release could not be submitted. The available amount may have changed. Review the deposit details and try again.'
      );
    } finally {
      processing.current = false;
      setIsProcessing(false);
    }
  };

  return (
    <AgxModal
      title="Early Release of Deposit"
      titleIcon={
        <Icon_FeaturedIcon
          icon={<Icon_AlertCircleOutline type="Danger" />}
          color="var(--text-error)"
          backgroundColor="var(--bg-error-light)"
        />
      }
      type="single"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      actionButtons={
        <AgxSpacer direction="horizontal" gap="spacing-xl" contentJustification="center" fill>
          <AgxButton
            id="btnCancelEarlyRelease"
            text={releaseSubmitted ? 'Close' : 'Cancel'}
            variant="hollow"
            disabled={isProcessing}
            onClick={() => void closeAndCleanUp()}
            stretch
          />
          <AgxButton
            id="btnConfirmEarlyRelease"
            text="Confirm"
            variant="primary"
            danger
            disabled={!canSubmit}
            onClick={() => void handleConfirm()}
            stretch
          />
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="vertical"
          gap="md"
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxFileUpload
            title="Buyer Solicitor Confirmation"
            id="fileBuyerSolicitorEarlyDeposit"
            documentType={DocumentTypes.BuyerSolicitorEarlyDeposit}
            buttonText="Choose file"
            fileData={buyerSolicitorConfirmationFiles}
            onValueChanged={({ value }) => setBuyerSolicitorConfirmationFiles(value ?? [])}
            getAuthToken={getAuthToken}
            campaignId={campaign?.campaignId || ''}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
            validate={showValidation}
          />
          <AgxFileUpload
            title="Vendor Solicitor Confirmation"
            id="fileVendorSolicitorEarlyDeposit"
            documentType={DocumentTypes.VendorSolicitorEarlyDeposit}
            buttonText="Choose file"
            fileData={vendorSolicitorConfirmationFiles}
            onValueChanged={({ value }) => setVendorSolicitorConfirmationFiles(value ?? [])}
            getAuthToken={getAuthToken}
            campaignId={campaign?.campaignId || ''}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
            validate={showValidation}
          />
          <AgxTextInput
            id="earlyReleaseAmount"
            label="Amount to be released"
            type="currency"
            stretch
            required
            hideOptionalText
            defaultValue={amountInput}
            onChange={setAmountInput}
            errorText={showValidation || amountInput ? amountError : undefined}
            hintText={`Available to release: ${availableAmount.toLocaleString('en-AU', {
              style: 'currency',
              currency: 'AUD',
            })}`}
          />
          <AgxRadioGroup
            id="destinationOfTransfer"
            label="Destination of transfer"
            layout="vertical"
            options={destinationOptions}
            value={destinationOfTransfer}
            onChange={(value) =>
              setDestinationOfTransfer(
                value as StaffCreateContractDepositReleaseRequestDestinationOfTransfer
              )
            }
            required
            validate={showValidation}
            errorText={
              showValidation && !destinationOfTransfer
                ? 'Select destination of transfer'
                : undefined
            }
          />
          {destinationOfTransfer ===
            StaffCreateContractDepositReleaseRequestDestinationOfTransfer.Other && (
            <AgxTextInput
              id="earlyReleaseOtherRecipient"
              label="Recipient"
              type="text"
              stretch
              required
              hideOptionalText
              maxLength={200}
              defaultValue={otherRecipient}
              onChange={setOtherRecipient}
              errorText={showValidation || otherRecipient ? otherRecipientError : undefined}
            />
          )}
          {submitError && (
            <AgxNotificationBox
              type="danger"
              heading="Early release not submitted"
              body={submitError}
            />
          )}
          {cleanupError && (
            <div role="alert">
              <AgxNotificationBox
                type="danger"
                heading={
                  releaseSubmitted
                    ? 'Unable to remove unused confirmation PDFs'
                    : 'Unable to cancel early release'
                }
                body={cleanupError}
              />
            </div>
          )}
        </AgxSpacer>
      }
      isOpen={isOpen}
      setIsDialogOpen={(nextValue) => {
        if (!nextValue && !processing.current) void closeAndCleanUp();
      }}
    />
  );
};

export default EarlyReleaseModal;
